import { useCallback } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard, Package, FileText, Users, CreditCard,
  Building2, Wallet, ShoppingCart, Monitor, BarChart3,
  Settings, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Shield, CalendarClock, FileSignature, Wrench,
} from 'lucide-react'
import { useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useUIStore } from '@/stores/ui.store'
import { useAuthStore } from '@/stores/auth.store'
import { useServisModulu } from '@/hooks/useGenelAyar'
import { useTabsStore } from '@/stores/tabs.store'
import { apiClient } from '@/services/api.client'

// Masaüstü (Tauri): sidebar tıklamaları sekme açar (openTab); web: klasik NavLink.
const isTauri = typeof window !== 'undefined' && !!(window as { __TAURI__?: unknown }).__TAURI__

type NavChild = { to: string; label: string; end?: boolean; permission?: string }
type NavGroup = { type: 'group'; label: string; icon: LucideIcon; basePath: string; extraPaths?: string[]; children: NavChild[]; permission?: string; conditional?: 'ebelge' }
type NavItem = { type: 'item'; to: string; label: string; icon: LucideIcon; end?: boolean; permission?: string; conditional?: 'servis' }
type NavEntry = NavGroup | NavItem

const navConfig: NavEntry[] = [
  { type: 'item', to: '/dashboard', label: 'Ana Sayfa', icon: LayoutDashboard, end: true },
  {
    type: 'group', label: 'Stok', icon: Package, basePath: '/stok', extraPaths: ['/depo'], permission: 'stok.goruntule',
    children: [
      { to: '/stok', label: 'Stok Yönetimi', end: true, permission: 'stok_liste.goruntule' },
      { to: '/stok/hareketler', label: 'Stok Hareketleri', end: true, permission: 'stok_hareketler.goruntule' },
      { to: '/stok/giris-cikis', label: 'Depo Giriş-Çıkış Fişi', permission: 'stok_depo_fis.goruntule' },
      { to: '/stok/sayim', label: 'Sayım İşlemleri', end: true, permission: 'stok_sayim.goruntule' },
      { to: '/depo/transfer', label: 'Depolar Arası Transfer', end: true, permission: 'stok_depo_transfer.goruntule' },
    ],
  },
  {
    type: 'group', label: 'Cari Hesap', icon: Users, basePath: '/cari', permission: 'cari.goruntule',
    children: [
      { to: '/cari', label: 'Cari Hesap Yönetimi', end: true, permission: 'cari_liste.goruntule' },
      { to: '/cari/hareketler', label: 'Cari Hesap Hareketleri', end: true, permission: 'cari_hareketler.goruntule' },
    ],
  },
  {
    type: 'group', label: 'Fatura', icon: FileText, basePath: '/fatura', permission: 'fatura.goruntule',
    children: [
      { to: '/fatura/alis-irsaliye', label: 'Alış İrsaliyesi', permission: 'fatura_alis_irsaliye.goruntule' },
      { to: '/fatura/satis-irsaliye', label: 'Satış İrsaliyesi', permission: 'fatura_satis_irsaliye.goruntule' },
      { to: '/fatura/alis', label: 'Alış Faturası', permission: 'fatura_alis.goruntule' },
      { to: '/fatura/satis', label: 'Satış Faturası', permission: 'fatura_satis.goruntule' },
      { to: '/fatura/alis-iade', label: 'Alış İade Faturası', permission: 'fatura_alis_iade.goruntule' },
      { to: '/fatura/satis-iade', label: 'Satış İade Faturası', permission: 'fatura_satis_iade.goruntule' },
    ],
  },
  {
    type: 'group', label: 'Sipariş', icon: ShoppingCart, basePath: '/siparis', permission: 'siparis.goruntule',
    children: [
      { to: '/siparis', label: 'Satış Siparişi', end: true, permission: 'siparis_satis.goruntule' },
      { to: '/siparis/satin-alma', label: 'Satın Alma Siparişi', end: true, permission: 'siparis_alis.goruntule' },
    ],
  },
  { type: 'item', to: '/banka', label: 'Banka', icon: Building2, end: true, permission: 'banka.goruntule' },
  { type: 'item', to: '/kasa', label: 'Kasa', icon: Wallet, end: true, permission: 'kasa.goruntule' },
  { type: 'item', to: '/cek', label: 'Çek/Senet', icon: CreditCard, end: true, permission: 'cek.goruntule' },
  { type: 'item', to: '/taksit', label: 'Taksit Takibi', icon: CalendarClock, end: false, permission: 'taksit.goruntule' },
  { type: 'item', to: '/servis', label: 'Servis / Arıza', icon: Wrench, end: false, conditional: 'servis' },
  {
    type: 'group', label: 'Hızlı Satış', icon: Monitor, basePath: '/pos', extraPaths: ['/hizli-satis'], permission: 'pos.goruntule',
    children: [
      { to: '/pos', label: 'Hızlı Satış Ekranı', end: true, permission: 'pos_satis.goruntule' },
      { to: '/hizli-satis/gecmis', label: 'Satış Geçmişi', end: true, permission: 'pos_gecmis.goruntule' },
    ],
  },
  {
    type: 'group', label: 'Raporlar', icon: BarChart3, basePath: '/raporlar', permission: 'raporlar.goruntule',
    children: [
      { to: '/raporlar', label: 'Standart Raporlar', end: true, permission: 'raporlar.goruntule' },
      { to: '/raporlar/builder', label: 'Rapor Builder', end: false, permission: 'raporlar.goruntule' },
    ],
  },
  {
    // Yalnızca firma e-Belge kullanımı AKTİFSE görünür (conditional) + RBAC ile korunur.
    type: 'group', label: 'eBelge İşlemler', icon: FileSignature, basePath: '/ebelge', conditional: 'ebelge',
    children: [
      { to: '/ebelge/e-fatura', label: 'e-Fatura', end: true, permission: 'ebelge_efatura.goruntule' },
      { to: '/ebelge/e-arsiv', label: 'e-Arşiv', end: true, permission: 'ebelge_earsiv.goruntule' },
      { to: '/ebelge/e-irsaliye', label: 'e-İrsaliye', end: true, permission: 'ebelge_eirsaliye.goruntule' },
    ],
  },
  { type: 'item', to: '/ayarlar', label: 'Ayarlar', icon: Settings, permission: 'ayarlar.goruntule' },
]

const ADMIN_ITEMS = [
  { to: '/admin', label: 'Dashboard', end: true },
  { to: '/admin/hesaplar', label: 'Hesaplar' },
  { to: '/admin/firmalar', label: 'Firma Yönetimi' },
  { to: '/admin/planlar', label: 'Plan Yönetimi' },
  { to: '/admin/yedekler', label: 'Yedekleme' },
]

// ── Shared child item renderer (kullanılan hem desktop hem mobil içeriğinde) ──
function ChildItem({ child, icon, activePath, onClick }: { child: NavChild; icon?: LucideIcon; activePath: string; onClick: () => void }) {
  const openTab = useTabsStore((s) => s.openTab)
  const childActive = child.end
    ? activePath === child.to
    : activePath === child.to || activePath.startsWith(child.to + '/')
  return (
    <NavLink
      to={child.to}
      end={child.end}
      onClick={(e) => { if (isTauri) { e.preventDefault(); openTab(child.to, child.label, icon) } onClick() }}
      className={cn(
        'flex items-center gap-2 pl-9 pr-3 py-1.5 rounded-md text-sm transition-colors select-none',
        childActive
          ? 'text-primary font-medium bg-primary/10'
          : 'text-text-muted hover:text-text-primary hover:bg-bg-elevated'
      )}
    >
      <span className="w-1 h-1 rounded-full bg-current shrink-0 opacity-60" />
      <span className="truncate">{child.label}</span>
    </NavLink>
  )
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, mobileSidebarOpen, closeMobileSidebar } = useUIStore()
  const { kullanici, hasPermission } = useAuthStore()
  const isSuperAdmin = kullanici?.rol === 'super_admin'
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const openTab = useTabsStore((s) => s.openTab)
  const activeTab = useTabsStore((s) => s.tabs.find((t) => t.id === s.activeId))

  // Aktif yol: masaüstünde aktif sekmenin iç yolu, web'de tarayıcı konumu.
  const activePath = isTauri ? (activeTab?.currentPath ?? '') : location.pathname

  const handleNavClick = useCallback(() => {
    qc.invalidateQueries()
    closeMobileSidebar()
  }, [qc, closeMobileSidebar])

  // Masaüstünde sekme aç, web'de normal NavLink navigasyonuna bırak.
  const handleItemClick = useCallback((e: React.MouseEvent, to: string, label: string, icon?: LucideIcon) => {
    if (isTauri) { e.preventDefault(); openTab(to, label, icon) }
    handleNavClick()
  }, [openTab, handleNavClick])

  const isGroupActive = (e: NavGroup) =>
    activePath.startsWith(e.basePath) ||
    (e.extraPaths?.some((p) => activePath.startsWith(p)) ?? false)

  // Firma e-Belge kullanım durumu — "eBelge İşlemler" grubu yalnızca aktifse görünür.
  const { data: ebelgeDurum } = useQuery({
    queryKey: ['firma-ebelge'],
    queryFn: () => (apiClient.get('/firma-ebelge') as Promise<{ data: { durum: 'pasif' | 'aktif' } }>).then((r) => r.data),
    staleTime: 60_000,
  })
  const ebelgeAktif = ebelgeDurum?.durum === 'aktif'
  const servisAktif = useServisModulu()

  const visibleNav = navConfig.filter(
    (e) =>
      (!e.permission || hasPermission(e.permission)) &&
      (e.type !== 'group' || e.conditional !== 'ebelge' || ebelgeAktif) &&
      (e.type !== 'item' || e.conditional !== 'servis' || servisAktif),
  )

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const result: Record<string, boolean> = {}
    navConfig.forEach((e) => {
      if (e.type === 'group') result[e.label] = isGroupActive(e)
    })
    return result
  })

  function toggleGroup(label: string) {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }))
  }

  // Tablet (md-lg) ve collapsed modda: grup başlığına tıklanınca ilk alt öğeye git
  function handleGroupClick(entry: NavGroup, firstChild?: NavChild) {
    if (sidebarCollapsed || window.innerWidth < 1024) {
      const to = firstChild?.to ?? entry.basePath
      if (isTauri) openTab(to, firstChild?.label ?? entry.label, entry.icon)
      else navigate(to)
      handleNavClick()
      return
    }
    toggleGroup(entry.label)
  }

  /* ── DESKTOP Sidebar içeriği (md+, icon-only at md-lg, full at lg+) ── */
  const desktopContent = (
    <>
      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden">
        {visibleNav.map((entry) => {
          if (entry.type === 'item') {
            const Icon = entry.icon
            const active = entry.end
              ? activePath === entry.to
              : activePath.startsWith(entry.to)
            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.end}
                onClick={(e) => handleItemClick(e, entry.to, entry.label, entry.icon)}
                title={entry.label}
                className={cn(
                  'flex items-center gap-3 mx-2 px-2 py-2 rounded-md text-base transition-colors select-none',
                  // tablet: center; desktop full: start
                  'justify-center',
                  !sidebarCollapsed && 'lg:justify-start lg:px-3',
                  active
                    ? 'bg-primary/15 text-primary font-medium'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {/* tablet: hidden; desktop full: shown */}
                <span className={cn('truncate hidden', !sidebarCollapsed && 'lg:inline')}>
                  {entry.label}
                </span>
              </NavLink>
            )
          }

          // Group
          const Icon = entry.icon
          const groupActive = isGroupActive(entry)
          const isOpen = openGroups[entry.label] ?? false
          const visibleChildren = entry.children.filter((c) => !c.permission || hasPermission(c.permission))
          if (visibleChildren.length === 0) return null
          const firstChild = visibleChildren[0]

          return (
            <div key={entry.label} className="mx-2">
              <button
                onClick={() => handleGroupClick(entry, firstChild)}
                title={entry.label}
                className={cn(
                  'w-full flex items-center gap-3 px-2 py-2 rounded-md text-base transition-colors select-none text-left',
                  'justify-center',
                  !sidebarCollapsed && 'lg:justify-start lg:px-3',
                  groupActive
                    ? 'text-primary font-medium'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className={cn('truncate flex-1 hidden', !sidebarCollapsed && 'lg:inline')}>
                  {entry.label}
                </span>
                {!sidebarCollapsed && (
                  <>
                    {isOpen
                      ? <ChevronUp className="w-3.5 h-3.5 shrink-0 text-text-muted hidden lg:block" />
                      : <ChevronDown className="w-3.5 h-3.5 shrink-0 text-text-muted hidden lg:block" />}
                  </>
                )}
              </button>

              {/* Children — tablet'te gizli, desktop full'da açık */}
              {isOpen && !sidebarCollapsed && (
                <div className="hidden lg:block mb-1">
                  {visibleChildren.map((child) => (
                    <ChildItem key={child.to} child={child} icon={entry.icon} activePath={activePath} onClick={handleNavClick} />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Admin menüsü */}
      {isSuperAdmin && (
        <div className="px-2 pt-1 pb-2 border-t border-bg-border mt-1">
          <p className={cn('px-3 py-1 text-xs font-semibold text-text-muted uppercase tracking-wider hidden', !sidebarCollapsed && 'lg:block')}>
            Admin
          </p>
          {ADMIN_ITEMS.map(({ to, label, end }) => {
            const adminActive = end ? activePath === to : activePath.startsWith(to)
            return (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={(e) => handleItemClick(e, to, label, Shield)}
              title={label}
              className={cn(
                'flex items-center gap-3 px-2 py-2 rounded-md text-base transition-colors select-none',
                'justify-center',
                !sidebarCollapsed && 'lg:justify-start lg:px-3',
                adminActive
                  ? 'bg-primary/15 text-primary font-medium'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
              )}
            >
              <Shield className="w-4 h-4 shrink-0" />
              <span className={cn('truncate hidden', !sidebarCollapsed && 'lg:inline')}>{label}</span>
            </NavLink>
            )
          })}
        </div>
      )}

      {/* Collapse Toggle — sadece desktop (lg+) */}
      <div className="p-2 border-t border-bg-border hidden lg:block">
        <button
          onClick={toggleSidebar}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-text-muted',
            'hover:text-text-primary hover:bg-bg-elevated transition-colors',
            sidebarCollapsed && 'justify-center'
          )}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <ChevronLeft className="w-4 h-4" />
              <span>Küçült</span>
            </>
          )}
        </button>
      </div>
    </>
  )

  /* ── MOBİL Sidebar içeriği (< md, her zaman tam menü) ── */
  const mobileContent = (
    <>
      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden">
        {visibleNav.map((entry) => {
          if (entry.type === 'item') {
            const Icon = entry.icon
            const active = entry.end
              ? activePath === entry.to
              : activePath.startsWith(entry.to)
            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.end}
                onClick={(e) => handleItemClick(e, entry.to, entry.label, entry.icon)}
                className={cn(
                  'flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-base transition-colors select-none',
                  active
                    ? 'bg-primary/15 text-primary font-medium'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{entry.label}</span>
              </NavLink>
            )
          }

          const Icon = entry.icon
          const groupActive = isGroupActive(entry)
          const isOpen = openGroups[entry.label] ?? false
          const visibleChildren = entry.children.filter((c) => !c.permission || hasPermission(c.permission))
          if (visibleChildren.length === 0) return null

          return (
            <div key={entry.label} className="mx-2">
              <button
                onClick={() => toggleGroup(entry.label)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-md text-base transition-colors select-none text-left',
                  groupActive
                    ? 'text-primary font-medium'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                <span className="truncate flex-1">{entry.label}</span>
                {isOpen
                  ? <ChevronUp className="w-3.5 h-3.5 shrink-0 text-text-muted" />
                  : <ChevronDown className="w-3.5 h-3.5 shrink-0 text-text-muted" />}
              </button>
              {isOpen && (
                <div className="mb-1">
                  {visibleChildren.map((child) => (
                    <ChildItem key={child.to} child={child} icon={entry.icon} activePath={activePath} onClick={handleNavClick} />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {isSuperAdmin && (
        <div className="px-2 pt-1 pb-2 border-t border-bg-border mt-1">
          <p className="px-3 py-1 text-xs font-semibold text-text-muted uppercase tracking-wider">Admin</p>
          {ADMIN_ITEMS.map(({ to, label, end }) => {
            const adminActive = end ? activePath === to : activePath.startsWith(to)
            return (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={(e) => handleItemClick(e, to, label, Shield)}
              className={cn(
                'flex items-center gap-3 px-3 py-2 rounded-md text-base transition-colors select-none',
                adminActive
                  ? 'bg-primary/15 text-primary font-medium'
                  : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated',
              )}
            >
              <Shield className="w-4 h-4 shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
            )
          })}
        </div>
      )}
    </>
  )

  return (
    <>
      {/* ── Desktop Sidebar (md ve üstü) ── */}
      <aside
        className={cn(
          'fixed top-header left-0 h-[calc(100vh-56px)] bg-bg-surface border-r border-bg-border',
          'hidden md:flex flex-col transition-all duration-200 ease-in-out z-30 print:hidden',
          // md-lg: her zaman icon genişliği; lg+: collapsed durumuna göre
          'w-sidebar-icon',
          !sidebarCollapsed && 'lg:w-sidebar',
        )}
      >
        {desktopContent}
      </aside>

      {/* ── Mobil Overlay (md altı) ── */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 md:hidden print:hidden',
          mobileSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={closeMobileSidebar}
      />

      {/* ── Mobil Drawer (md altı) ── */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full w-[280px] bg-bg-surface border-r border-bg-border',
          'flex flex-col z-50 transition-transform duration-200 ease-in-out md:hidden print:hidden',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="h-header flex items-center gap-2 px-4 border-b border-bg-border shrink-0">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <span className="text-white text-xs font-bold">N</span>
          </div>
          <span className="font-semibold text-text-primary text-md">iNova Muhasebe</span>
        </div>
        {mobileContent}
      </aside>
    </>
  )
}
